import React, { useState, use } from 'react';
import { Favorite } from '@mui/icons-material';
import { PicassoTitle } from '../ui/BrandingComponents';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import { TogglePage } from '../dashboard/ui/TogglePage';
import { useResponsiveColumns, VIDEO_GRID_COLUMNS, IMAGE_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns';
import { useFavouriteVideos, useFavouriteImages } from '@/hooks/querry_hooks/common/useFavouriteQuery';
import videoKeys from '@/api/querry_keys/videoKeys';
import imageKeys from '@/api/querry_keys/imageKeys';
import { ToastNotification } from '@/components/ui/common/ToastNotification';
import { useMediaPageHandlers } from '@/hooks/common/useMediaPageHandlers.tsx';
import { VideoContentRenderer, ImageContentRenderer, MediaPageContent } from '@/components/common/MediaContentRenderer';

interface FavouritesPageProps {
  userEmail: string | null;
  isAdmin: boolean;
}

/**
 * FavouritesPage - Displays user's favorited videos and images
 * 
 * Features:
 * - Toggle between Videos and Images tabs
 * - Grid display of favorited content
 * - Responsive design
 * - Empty state when no favorites
 */
const FavouritesPage: React.FC<FavouritesPageProps> = ({ userEmail, isAdmin }) => {
  const { isMobile } = use(DeviceContext) ?? { isMobile: false };
  
  // State for tab selection
  const [pageValue, setPageValue] = useState<'Videos' | 'Images'>('Videos');
  
  // Fetch favourite videos using React Query
  const { data: favouriteVideosData, isLoading: isLoadingVideos, refetch: refetchVideos } = useFavouriteVideos();
  const favouriteVideos = favouriteVideosData?.videos ?? [];
  
  // Fetch favourite images using React Query
  const { data: favouriteImagesData, isLoading: isLoadingImages, refetch: refetchImages } = useFavouriteImages();
  const favouriteImages = favouriteImagesData?.images ?? [];

  // Use shared media page handlers
  const {
    selectedVideo,
    selectedImage,
    favouriteToastOpen,
    favouriteToastMessage,
    setFavouriteToastOpen,
    archiveToastOpen,
    archiveToastMessage,
    setArchiveToastOpen,
    handleVideoClick,
    handleImageClick,
    getStatusBadge,
    handleFavouriteSuccess,
    handleArchiveSuccess,
    formatFileSize,
    formatDate,
    handleRequestDelete,
    handleRequestImageDelete,
    handleDeleteVideo,
    handleDeleteImage,
    closeModals,
  } = useMediaPageHandlers(
    userEmail,
    refetchVideos,
    refetchImages,
    {
      videos: [videoKeys.favourites(), videoKeys.all],
      images: [imageKeys.favourites(), imageKeys.all],
    }
  );

  const videoColumns = useResponsiveColumns(VIDEO_GRID_COLUMNS);
  const imageColumns = useResponsiveColumns(IMAGE_GRID_COLUMNS);

  /**
   * Handles page refresh when title is clicked
   */
  const handleRefresh = () => {
    globalThis.location.reload();
  };

  /**
   * Render videos content
   */
  const renderVideosContent = () => (
    <VideoContentRenderer
      videos={favouriteVideos}
      isLoading={isLoadingVideos}
      columns={videoColumns}
      config={{
        icon: <Favorite className="w-8 h-8 text-pink-600" />,
        iconBgColor: "bg-pink-100",
        title: "No Favorite Videos",
        description: "Videos you favorite will appear here. Click the heart icon on any video to add it to your favorites!",
        skeletonKeyPrefix: "fav-video-skeleton"
      }}
      onVideoClick={handleVideoClick}
      getStatusBadge={getStatusBadge}
      // isAdmin={isAdmin}
      isFavouritesPage={true}
      onFavouriteSuccess={(isFavourited: boolean) => handleFavouriteSuccess(isFavourited, 'video')}
      // onArchiveSuccess={(isArchived: boolean) => handleArchiveSuccess(isArchived, 'video')}
    />
  );

  /**
   * Render images content
   */
  const renderImagesContent = () => (
    <ImageContentRenderer
      images={favouriteImages}
      isLoading={isLoadingImages}
      columns={imageColumns}
      config={{
        icon: <Favorite className="w-8 h-8 text-pink-600" />,
        iconBgColor: "bg-pink-100",
        title: "No Favorite Images",
        description: "Images you favorite will appear here. Click the heart icon on any image to add it to your favorites!",
        skeletonKeyPrefix: "fav-image-skeleton"
      }}
      onImageClick={handleImageClick}
      showUserEmail={isAdmin}
      isAdmin={isAdmin}
      isFavouritesPage={true}
      onFavouriteSuccess={(isFavourited: boolean) => handleFavouriteSuccess(isFavourited, 'image')}
      onArchiveSuccess={(isArchived: boolean) => handleArchiveSuccess(isArchived, 'image')}
    />
  );

  return (
    <div className={`flex-1 w-full px-4 sm:px-6 ${isMobile ? 'py-0' : 'pt-[23px]'} sm:py-12 bg-white`}>
      {/* Page title with Picasso branding */}
      <div className="text-center mb-6">
        <div className="w-full flex justify-center">
          <div className="relative">
            {!isMobile && 
              <PicassoTitle 
                text="Favourites"
                size="lg"
                gradient="picassoTitle"
                onClick={handleRefresh}
                style={{ 
                  maxWidth: 'fit-content',
                  cursor: 'pointer'
                }}
                className="mb-[1.5rem]"
              />
            }
          </div>
        </div>

        {/* Tab Toggle - Videos / Images */}
        <div className="flex justify-center mb-10 mt-10">
          <TogglePage 
            value={pageValue} 
            handleChange={(_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
              if (newValue === 'Videos' || newValue === 'Images') {
                setPageValue(newValue);
              }
            }}
            showTranscripts={false}
          />
        </div>
      </div>

      <MediaPageContent
        pageValue={pageValue}
        renderVideosContent={renderVideosContent}
        renderImagesContent={renderImagesContent}
        selectedVideo={selectedVideo}
        selectedImage={selectedImage}
        onClose={closeModals}
        userEmail={userEmail}
        isAdmin={isAdmin}
        getStatusBadge={getStatusBadge}
        formatDate={formatDate}
        formatFileSize={formatFileSize}
        onRequestDelete={handleRequestDelete}
        onRequestImageDelete={handleRequestImageDelete}
        onDelete={isAdmin ? handleDeleteVideo : undefined}
        onImageDelete={isAdmin ? handleDeleteImage : undefined}
      />

      {/* Favourite Toast Notification */}
      <ToastNotification
        open={favouriteToastOpen}
        message={favouriteToastMessage}
        onClose={() => setFavouriteToastOpen(false)}
      />

       {/* Archive Toast Notification */}
      <ToastNotification
        open={archiveToastOpen}
        message={archiveToastMessage}
        onClose={() => setArchiveToastOpen(false)}
      />
    </div>
  );
};

export default FavouritesPage;
